import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
// The chips under a sent message: what the person attached when they sent it (the same quiet chips the composer showed).
import { useMemo } from 'react';
import { useAuiState, getExternalStoreMessages } from '@assistant-ui/react';
import { useNamespaceCopy } from '@/i18n/ns';
import buildWatchCopy from '@/i18n/ns/build-watch';
import { chipsFromParts, useMessageContext, type MessageContextChip } from '@/lib/message-context';
import type { WorkspaceState } from './context';
import { useOptionalWorkspace } from './context';
import { contextReferenceLabel } from './context-reference-chips';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { appSelectionLabel } from './apps/preview/live-build-model';
import storefrontCopy from '@/i18n/ns/storefront';

/** What the composer currently shows as attached, as data to keep with the message it is about to be sent with. */
export function turnContextChips(
  ws: Pick<WorkspaceState, 'turnContext' | 'data' | 'contextReferences'>,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const context = ws.turnContext;
  const chips: MessageContextChip[] = [];
  const refs = [
    ['fileIds', 'file'],
    ['sourceIds', 'source'],
    ['resultIds', 'result'],
  ] as const;
  for (const [field, kind] of refs)
    for (const id of context[field])
      chips.push({ kind, label: contextReferenceLabel(ws as WorkspaceState, field, id, uiText) });
  if (context.selection) chips.push({ kind: 'passage' });
  if (context.appSelection)
    chips.push({
      kind: 'selection',
      ...(context.appSelection.text ? { text: context.appSelection.text } : {}),
      ...(context.appSelection.role ? { role: context.appSelection.role } : {}),
      ...(context.appSelection.tag ? { tag: context.appSelection.tag } : {}),
    });
  if (context.storefrontSelection)
    chips.push({
      kind: 'selection',
      ...(context.storefrontSelection.text ? { text: context.storefrontSelection.text } : {}),
      tag: context.storefrontSelection.tag,
      of: 'assistant',
    });
  if (context.storefrontConversations)
    chips.push({ kind: 'conversations', label: context.storefrontConversations.label });
  return chips;
}

const ICON: Record<MessageContextChip['kind'], WorkspaceIconName> = {
  file: 'file',
  source: 'knowledge',
  result: 'results',
  passage: 'file',
  selection: 'pointer',
  conversations: 'chat',
};

export function MessageContextChips() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const workspace = useOptionalWorkspace();
  const buildCopy = useNamespaceCopy(buildWatchCopy);
  const storefrontText = useNamespaceCopy(storefrontCopy);
  const messageId = useAuiState((state) => state.message.id);
  // The stored message's own parts array (reference-stable); the chips are read from it below.
  const parts = useAuiState((state) => {
    const original = getExternalStoreMessages(state.message)[0] as
      | { parts?: unknown[] }
      | undefined;
    return original?.parts ?? null;
  });
  const stored = useMemo(() => chipsFromParts(parts), [parts]);
  const remembered = useMessageContext(workspace?.threadId ?? null, messageId);
  const chips = stored ?? remembered;
  if (!chips?.length) return null;
  return (
    <ul
      className="ws-message-context"
      data-testid="message-context"
      aria-label={uiText('Attached to this message')}
    >
      {chips.map((chip, index) => {
        const label =
          chip.kind === 'selection'
            ? chip.of === 'assistant' && !chip.text
              ? storefrontText.selectedAssistantPart
              : appSelectionLabel(
                  { tag: chip.tag ?? '', ...(chip.text ? { text: chip.text } : {}) },
                  buildCopy,
                )
            : chip.kind === 'passage'
              ? uiText('Selected passage')
              : chip.label;
        return (
          <li key={`${chip.kind}:${index}`} className="ws-context-chip" data-kind={chip.kind}>
            <WorkspaceIcon name={ICON[chip.kind]} size={13} />
            {label}
          </li>
        );
      })}
    </ul>
  );
}
